/**
 * Lazy Loading Components - Main Export File
 * 
 * This file provides a simple interface to all lazy-loaded components.
 * The actual implementation is split across multiple focused modules.
 */

// Re-export from split modules for better organization and smaller file size
export {
  createLazyComponent,
  type LazyComponentConfig
} from './lazyComponents/core';

export {
  // Page components
  LazyCreateRecipePage,
  LazySearchPage,
  LazyMealPlannerPage,
  LazyShoppingListPage,
  LazyFavoritesPage,
  LazyProfilePage,
  // Feature components
  LazyRecipeForm,
  LazyInstructionEditor,
  LazyMealPlanningView,
  LazyAdvancedRecipePicker,
  LazyRecipeListWithVirtualization,
  // Auth components
  LazyAuthRoutes,
  LazyLoginForm,
  // Service components - removed for bundle size optimization
  // LazyDataProvider,
  // LazyPerformanceMonitor
} from './lazyComponents/componentLoaders';

export {
  loadVirtualScrolling,
  loadDragAndDrop,
  loadSecurityUtils,
  loadDateUtils,
  loadWebVitals,
  loadNetworkServices,
  loadCacheServices
} from './lazyComponents/libraryLoaders';

export {
  preloadStrategies,
  executePreloadStrategy,
  initializePreloadSystem,
  type PreloadStrategy
} from './lazyComponents/preloadSystem';

// Critical components that should be preloaded immediately
export const preloadCriticalComponents = () => {
  const criticalImports = [
    import('../components/RecipeList'),
    import('../components/SearchBar'),
    import('../components/FilterPanel')
  ];
  
  // Start loading but don't block
  criticalImports.forEach(importPromise =>
    importPromise.catch(err => console.warn('Failed to preload:', err))
  );
};

// Bundle monitoring (lightweight version)
export const getBundleStats = () => {
  if (typeof performance === 'undefined') return null;
  
  try {
    const entries = performance.getEntriesByType('resource');
    const jsFiles = entries.filter((entry: any) => 
      entry.name.includes('.js') && !entry.name.includes('node_modules')
    );
    
    const totalSize = jsFiles.reduce((sum: number, file: any) => 
      sum + (file.transferSize || 0), 0
    );
    
    return {
      totalKB: Math.round(totalSize / 1024),
      fileCount: jsFiles.length
    };
  } catch {
    return null;
  }
};